import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/fetch-with-auth";

interface AttentionItem {
  type: "manual" | "drinks_no_ficha" | "cozinha_no_ficha";
  count: number;
  link: string;
  title: string;
}

interface AttentionItemsResponse {
  items: AttentionItem[];
  loading: boolean;
  error: Error | null;
}

export function useAttentionItems(): AttentionItemsResponse {
  const { hasModuleAccess } = useAuth();
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchAttentionItems() {
      try {
        setLoading(true);
        setError(null);
        const attentionItems: AttentionItem[] = [];

        // 1. Artigos do manual não confirmados (para todos usuários)
        if (hasModuleAccess("manual")) {
          const manualResponse = await fetchWithAuth("/api/v1/manual/updates");
          if (manualResponse.ok) {
            const manualData = await manualResponse.json();
            const unreadCount = manualData.data?.unread_count || 0;
            if (unreadCount > 0) {
              attentionItems.push({
                type: "manual",
                count: unreadCount,
                link: "/manual",
                title: unreadCount === 1 ? "Artigo do manual não lido" : "Artigos do manual não lidos",
              });
            }
          }
        }

        if (hasModuleAccess("products") || hasModuleAccess("fichas-tecnicas")) {
          const productsResponse = await fetchWithAuth("/api/v1/products");
          if (productsResponse.ok) {
            const productsData = await productsResponse.json();
            const products = productsData.data || [];
            const drinksWithoutFicha = products.filter(
              (p: any) => p.category_id === 7 && p.ficha_tecnica_id === null
            );
            if (drinksWithoutFicha.length > 0) {
              attentionItems.push({
                type: "drinks_no_ficha",
                count: drinksWithoutFicha.length,
                link: "/produtos?cats=7&chars=sem_ficha",
                title: drinksWithoutFicha.length === 1 ? "Drink sem ficha técnica" : "Drinks sem ficha técnica",
              });
            }
            const cozinhaWithoutFicha = products.filter(
              (p: any) => p.category_id === 1 && p.ficha_tecnica_id === null
            );
            if (cozinhaWithoutFicha.length > 0) {
              attentionItems.push({
                type: "cozinha_no_ficha",
                count: cozinhaWithoutFicha.length,
                link: "/produtos?cats=1&chars=sem_ficha",
                title: cozinhaWithoutFicha.length === 1 ? "Prato sem ficha técnica" : "Pratos sem ficha técnica",
              });
            }
          }
        }

        setItems(attentionItems);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to fetch attention items"));
      } finally {
        setLoading(false);
      }
    }

    fetchAttentionItems();
  }, [hasModuleAccess]);

  return { items, loading, error };
}
